
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Home, ArrowLeft, Search } from "lucide-react";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();

  // Function to check if a path is from an unrelated application
  const isUnrelatedPath = (path: string): boolean => {
    const unrelatedPhrases = [
      'Handlo', 'sc-infra', 'services', 'Gestione', 
      'Could An AI Business Generate', 'Revenue', 'Staff',
      'kawachi', 'Kawachi', 'ai-business',
      '.html', '.php', '/wp-', '/admin/', 'Project not found'
    ];
    
    return unrelatedPhrases.some(phrase => 
      path.toLowerCase().includes(phrase.toLowerCase())
    );
  }

  useEffect(() => {
    // Log the 404 error for tracking
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
    
    // Force navigation to home page for potentially harmful 404 errors
    if (isUnrelatedPath(location.pathname)) {
      console.warn('Suspicious 404 path detected, redirecting to home page immediately');
      navigate("/", { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center px-4 animate-fade-up max-w-md mx-auto">
        <h1 className="text-8xl font-bold text-primary mb-4">404</h1>
        <p className="text-xl text-muted-foreground mb-6">
          {t('notFound.title', language)}
        </p>
        <p className="text-sm text-muted-foreground mb-8 opacity-75">
          {t('notFound.subtitle', language)} {location.pathname}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button 
            onClick={() => navigate("/")}
            className="gap-2"
            size="lg"
          >
            <Home size={18} />
            {t('notFound.home', language)}
          </Button>
          
          <Button 
            onClick={() => navigate(-1)}
            variant="outline"
            className="gap-2"
            size="lg"
          >
            <ArrowLeft size={18} />
            {t('notFound.back', language)}
          </Button>
          
          <Button 
            onClick={() => navigate("/generate")}
            variant="secondary"
            className="gap-2"
            size="lg"
          >
            <Search size={18} />
            {t('notFound.createEntry', language)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
